import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import GavelIcon from '@mui/icons-material/Gavel';
import DescriptionIcon from '@mui/icons-material/Description';
import ContactsIcon from '@mui/icons-material/Contacts';
import BusinessIcon from '@mui/icons-material/Business';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import LinkIcon from '@mui/icons-material/Link';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArticleIcon from '@mui/icons-material/Article';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EmailIcon from '@mui/icons-material/Email';
import AppsIcon from '@mui/icons-material/Apps';
import { useNavigate } from '../../../hooks/useNavigate';
import { MegaMenuDropdown } from './mega-menu-dropdown';
import { useMenusData } from '../../../hooks/useMenusData';

interface SubMenuItem {
  label: string;
  path: string;
  icon?: React.ReactElement;
}

interface MenuItem {
  label: string;
  path?: string;
  icon: React.ReactElement;
  subItems?: SubMenuItem[];
  isMegaMenu?: boolean;
}

const menuStructure: MenuItem[] = [
  {
    label: 'INÍCIO',
    path: '/',
    icon: <HomeIcon />
  },

  {
    label: 'O MUNICÍPIO',
    icon: <LocationCityIcon />,
    subItems: [
      { label: 'A Cidade', path: '/a-cidade', icon: <InfoIcon /> },
      { label: 'Estrutura Administrativa', path: '/estrutura-administrativa', icon: <BusinessIcon /> },
      { label: 'Projetos Governamentais', path: '/projetos-governamentais', icon: <AccountBalanceIcon /> },
    ],
  },
  {
    label: 'NOTÍCIAS',
    path: '/noticias',
    icon: <NewspaperIcon />
  },
  {
    label: 'DIÁRIO OFICIAL',
    icon: <DescriptionIcon />,
    subItems: [
      { label: 'Edições', path: '/diario-oficial', icon: <DescriptionIcon /> },
      { label: 'Publicações', path: '/publicacoes', icon: <ArticleIcon /> },
    ],
  },

  {
    label: 'SERVIÇOS',
    icon: <FlashOnIcon />,
    subItems: [
      { label: 'Perguntas Frequentes', path: '/perguntas-respostas', icon: <QuestionAnswerIcon /> },
      { label: 'Links Úteis', path: '/links-uteis', icon: <LinkIcon /> },
    ],
  },
  {
    label: 'TRANSPARÊNCIA',
    icon: <VisibilityIcon />,
    subItems: [
      { label: 'Contas Públicas', path: '/contas-publicas', icon: <VisibilityIcon /> },
      { label: 'Licitações', path: '/licitacoes', icon: <GavelIcon /> },
      { label: 'Contratos e Aquisições', path: '/contratos-aquisicoes', icon: <ArticleIcon /> },
      { label: 'Convênios e Repasses', path: '/convenios-repasses', icon: <HandshakeIcon /> },
      { label: 'Auditoria', path: '/auditoria', icon: <AssessmentIcon /> },
    ],
  },
  {
    label: 'MAIS OPÇÕES',
    icon: <AppsIcon />,
    isMegaMenu: true
  },
  {
    label: 'FALE CONOSCO',
    path: '/contato',
    icon: <EmailIcon />
  },
];

interface AppMenuProps {
  isMobile: boolean;
  open?: boolean;
  onClose: () => void;
}

export default function AppMenu({ isMobile, open = false, onClose }: AppMenuProps) {
  const navigate = useNavigate();
  const { menus } = useMenusData();
  const [anchorEl, setAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});
  const [mobileOpenMenus, setMobileOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [megaMenuAnchor, setMegaMenuAnchor] = useState<HTMLElement | null>(null);

  // Filtrar menu structure para incluir "MAIS OPÇÕES" apenas se houver menus
  const hasMenus = menus && menus.length > 0;
  const filteredMenuStructure = menuStructure.filter(item => {
    if (item.isMegaMenu) {
      return hasMenus;
    }
    return true;
  });

  const handleNavigation = (path: string) => {
    if (path.startsWith('/#')) {
      window.location.href = path;
    } else {
      navigate(path);
    }
    onClose();
    handleCloseMenu();
  };

  const handleOpenMenu = (label: string, event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl({ ...anchorEl, [label]: event.currentTarget });
  };

  const handleCloseMenu = () => {
    setAnchorEl({});
  };

  const handleMobileToggle = (label: string) => {
    setMobileOpenMenus({
      ...mobileOpenMenus,
      [label]: !mobileOpenMenus[label],
    });
  };

  const handleMegaMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMegaMenuAnchor(event.currentTarget);
    setMegaMenuOpen(true);
  };

  const handleMegaMenuClose = () => {
    setMegaMenuOpen(false);
  };

  // Desktop Menu with Dropdowns
  if (!isMobile) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {filteredMenuStructure.map((item) => (
            <Box key={item.label}>
              {item.isMegaMenu ? (
                // Mega Menu
                <Button
                  onClick={handleMegaMenuOpen}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '& .MuiSvgIcon-root': {
                        color: 'white',
                      },
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  {item.label}
                </Button>
              ) : item.subItems ? (
              // Menu with dropdown
              <>
                <Button
                  onClick={(e) => handleOpenMenu(item.label, e)}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    px: 2,
                    py: 1,
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '& .MuiSvgIcon-root': {
                        color: 'white',
                      },
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  {item.label}
                </Button>
                <Menu
                  anchorEl={anchorEl[item.label]}
                  open={Boolean(anchorEl[item.label])}
                  onClose={handleCloseMenu}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button',
                  }}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      mt: 1,
                      minWidth: 220,
                      borderRadius: 2,
                    },
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  {item.subItems.map((subItem) => (
                    <MenuItem
                      key={subItem.path}
                      onClick={() => handleNavigation(subItem.path)}
                      sx={{
                        py: 1.5,
                        px: 3,
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'primary.main',
                        },
                      }}
                    >
                      <Box sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {subItem.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              // Simple menu item
              <Button
                onClick={() => item.path && handleNavigation(item.path)}
                sx={{
                  color: 'text.primary',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                  },
                  transition: 'all 0.2s',
                }}
              >
                {item.label}
              </Button>
              )}
            </Box>
          ))}
        </Box>

        {/* Mega Menu Dropdown */}
        <MegaMenuDropdown
          open={megaMenuOpen}
          onClose={handleMegaMenuClose}
          anchorEl={megaMenuAnchor}
        />
      </>
    );
  }

  // Mobile Drawer with Expandable Lists
  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
        },
      }}
    >
      <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white' }}>
        <HomeIcon sx={{ fontSize: 40 }} />
      </Box>
      <Divider />
      <List>
        {filteredMenuStructure.map((item) => (
          <React.Fragment key={item.label}>
            {item.isMegaMenu ? (
              // Mega Menu no mobile
              <ListItem disablePadding>
                <ListItemButton onClick={handleMegaMenuOpen}>
                  <ListItemIcon sx={{ color: 'primary.main' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ) : item.subItems ? (
              // Menu with sub-items
              <>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleMobileToggle(item.label)}>
                    <ListItemIcon sx={{ color: 'primary.main' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    />
                    {mobileOpenMenus[item.label] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={mobileOpenMenus[item.label]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItemButton
                        key={subItem.path}
                        sx={{ pl: 4 }}
                        onClick={() => handleNavigation(subItem.path)}
                      >
                        {subItem.icon && (
                          <ListItemIcon sx={{ color: 'primary.light', minWidth: 40 }}>
                            {subItem.icon}
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={subItem.label}
                          primaryTypographyProps={{
                            fontSize: '0.813rem',
                            fontWeight: 500,
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              // Simple menu item
              <ListItem disablePadding>
                <ListItemButton onClick={() => item.path && handleNavigation(item.path)}>
                  <ListItemIcon sx={{ color: 'primary.main' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Mega Menu Dropdown para Mobile */}
      <MegaMenuDropdown
        open={megaMenuOpen}
        onClose={handleMegaMenuClose}
        anchorEl={megaMenuAnchor}
      />
    </Drawer>
  );
}
